<template>
  <div class="gallery-collection-item bordered-sm cursor-pointer" @click="openDetail">
    <div
      class="gallery-collection-item-img"
      :style="{
        backgroundImage: 'url(' + imgPath + ')',
      }"
    />

    <div
      class="gallery-collection-item-header"
      :style="{
        backgroundImage: 'url(' + pathImages + 'gallery/' + headerImage + ')',
      }"
    >
      <shrink-text
        :text="replacedItemName"
        container-class-list="header-first-line justify-center"
        :font-size="32"
      />
      <span v-if="isPremium" class="premium-text text-20">{{ $t('gallery.premium') }}</span>
    </div>

    <div class="gallery-collection-item-bottom-container">
      <progress-status
        v-if="isPicture && !isLocked"
        :percent="picture.percentage_completed || 0"
        class="progress-status-position"
      />

      <div v-if="showCountBox" class="puzzle-pieces-wrapper text-32">
        <div v-if="!isPicture && lockedPicturesInCollection > 0" class="lock-counter">
          {{ lockedPicturesInCollection }}<app-icon class="lock" icon-name="lock-sm" />
        </div>

        <div v-if="isPicture && picturePiecesCount > 0" class="puzzle-pieces-counter">
          <div>
            <span class="text-texts-standard-important">{{ picture.pieces_completed }}</span
            ><span>/{{ picturePiecesCount }}</span>
          </div>

          <app-icon class="puzzle" icon-name="puzzle-md" />
        </div>

        <div
          v-else-if="!isPicture && collectionPicturesCount > 0"
          class="puzzle-pieces-counter flexing gap-2"
        >
          <div>
            <span>{{ collection.stats.pictures_completed }}</span
            ><span>/{{ collectionPicturesCount }}</span>
          </div>

          <app-icon class="icon-picture-counter" icon-name="gallery-images-md" />
        </div>
      </div>
    </div>

    <div
      v-if="isComplete"
      class="gallery-collection-item-footer text-32 font-bold text-texts-standard-dark"
      :style="{
        backgroundImage: 'url(' + pathImages + 'gallery/BG_ITEM_FOOTER_COMPLETED.avif)',
      }"
    >
      {{ $t('gallery.completed') }}
      <app-icon v-if="!isPickUpReward" class="done" icon-name="done-sm" />
    </div>

    <div v-if="isPickUpReward" class="pick-up-message-wrapper">
      <div class="pick-up-message text-36">{{ $t('gallery.claimReward') }}</div>
    </div>

    <div v-if="isNew && !isLocked" class="flag-new text-24 font-medium">
      {{ $t('gallery.new') }}
    </div>

    <div v-if="canUnlockForGems" class="gem-button-wrapper">
      <app-multi-button1
        class="gem-button"
        btn-type="credit"
        btn-icon="gems"
        btn-size="md"
        :btn-id="unlockBtnId"
        :btn-text="$t('gallery.unlock')"
        :btn-count="picture.parameters.price ?? 0"
        :popup-data="{ ...btnPopupData, btnValue: picture.parameters.price ?? 0 }"
        @action="unlockPicture(itemId, true)"
      />
    </div>

    <gallery-locked-item
      v-else-if="isPicture && isLocked"
      is-picture
      :discipline-id="disciplineId"
      :unlock-condition="picture.parameters?.unlock_condition_type || null"
      :unlock-condition-target="picture.parameters?.unlock_condition_value || null"
      :personal-best="`${personalBest}`"
    />

    <div v-if="showNotifications" class="notification text-22 font-bold">
      {{ isPicture ? '!' : collection.notifications }}
    </div>
  </div>
</template>

<script lang="ts">
import { pathImages } from '@/globalVariables'
import { defineComponent, type PropType } from 'vue'
import { mapActions } from 'pinia'
import type { Nullable } from '@/interfaces/utils'
import { formatResult } from '@/helpers'
import ShrinkText from '@/components/ShrinkText.vue'
import GalleryLockedItem from '@/components/Gallery/GalleryLockedItem.vue'
import AppMultiButton1 from '../GlobalComponents/AppMultiButton1.vue'
import ProgressStatus from './ProgressStatus.vue'
import type {
  GalleryCollection,
  GalleryCollectionParameters,
} from '@/interfaces/gallery/Collections'
import { useGalleryPictureStore } from '@/store/pinia/gallery/pictureStore'
import type { GalleryPicture } from '@/interfaces/gallery/Pictures'
import {
  GalleryCollectionState,
  GalleryPictureCategory,
  GalleryPictureState,
  GalleryPictureUnlockCondition,
} from '@/interfaces/gallery/enums'

interface ComponentData {
  pathImages: typeof pathImages
}

interface RouteObj {
  name: string
  params: { id: string }
  query: { redirectFrom: any }
}

const UNLOCK_BTN_ID = 'btn-unlock-gallery-picture'

export default defineComponent({
  name: 'GalleryCollectionItem',

  components: {
    ShrinkText,
    GalleryLockedItem,
    AppMultiButton1,
    ProgressStatus,
  },

  props: {
    isPicture: {
      type: Boolean,
      default: false,
    },

    collection: {
      type: Object as PropType<Nullable<GalleryCollection>>,
      default: () => null,
    },

    picture: {
      type: Object as PropType<Nullable<GalleryPicture>>,
      default: () => null,
    },

    personalBest: {
      type: [Number, String],
      default: 0,
    },
  },

  data(): ComponentData {
    return {
      pathImages,
    }
  },

  computed: {
    unlockBtnId(): string {
      return UNLOCK_BTN_ID
    },

    itemId(): string {
      return this.isPicture ? this.picture.id : this.collection.id
    },

    collectionPicturesCount(): number {
      if (this.isPicture) return 0

      return this.collection.parameters.pictures_in_collection
    },

    picturePiecesCount(): number {
      return this.picture.pieces_total
    },

    lockedPicturesInCollection(): number {
      return this.collectionPicturesCount - this.collection.stats.pictures_unlocked
    },

    picturePctCompleted(): number {
      return this.isPicture ? this.picture.percentage_completed : 0
    },

    showCountBox(): boolean {
      if (this.isLocked) return false
      if (this.isPicture) return this.picturePiecesCount > 0

      return this.lockedPicturesInCollection > 0 || this.collectionPicturesCount > 0
    },

    itemStatus(): string {
      return this.collection?.state || this.picture?.state || ''
    },

    isPickUpReward(): boolean {
      return this.itemStatus === GalleryCollectionState.CompletedUnclaimed
    },

    isLocked(): boolean {
      return (
        this.itemStatus === GalleryPictureState.Locked ||
        this.itemStatus === GalleryPictureState.Redeemable
      )
    },

    isComplete(): boolean {
      return (
        this.itemStatus === GalleryCollectionState.CompletedUnclaimed ||
        this.itemStatus === GalleryCollectionState.CompletedClaimed
      )
    },

    isNew(): boolean {
      if (
        this.isPicture &&
        this.picture?.parameters?.category === GalleryPictureCategory.Repeatable
      )
        return false
      return this.isPicture ? this.picture.seen === false : this.collection.stats.seen === false
    },

    showNotifications(): boolean {
      if (!this.picture?.notifications && !this.collection?.notifications) return false

      if (this.isPicture) return !!this.picture.notifications
      else {
        const notifNumber =
          typeof this.collection.notifications === 'string'
            ? parseInt(this.collection.notifications, 10)
            : this.collection.notifications
        return notifNumber > 0
      }
    },

    replacedItemName(): string {
      const name = this.isPicture ? this.picture.parameters.name : this.collection.parameters.name

      if (name === 'collection100Meters') {
        return this.$replacePlaceholder(this.$t(`gallery.${name}`), '%s', '100')
      }

      return this.$t(`gallery.${name}`)
    },

    isPremium(): boolean {
      return this.isPicture && !this.picture.parameters.is_free
    },

    canUnlockForGems(): boolean {
      return this.itemStatus === GalleryPictureState.Redeemable
    },

    headerImage(): string {
      return this.isPremium ? 'BG_ITEM_HEADER_PURPLE.jpg' : 'BG_ITEM_HEADER_BLUE.jpg'
    },

    disciplineId(): number {
      return this.isPicture
        ? this.picture.parameters.discipline
        : this.collection.parameters.discipline
    },

    imgPath(): string {
      if (this.isPicture) {
        const fileId = this.picture?.parameters?.id
        const inactiveStr = this.isLocked ? '-blur' : ''
        return `${pathImages}gallery/picture-thumbnail/gallery-picture-${fileId}-small${inactiveStr}.avif`
      }

      const fileId = this.collection?.parameters?.id
      const inactiveStr = this.isLocked ? '-blur' : ''
      return `${pathImages}gallery/collection-thumbnail/gallery-collection-${fileId}${inactiveStr}.avif`
    },

    btnPopupData(): any {
      return this.$filters.$confirmPopupData({
        title: this.replacedItemName,
        text: this.$t('gallery.unlockConfirmMessage'),
        btnId: UNLOCK_BTN_ID,
        btnText: this.$t('gallery.unlock'),
        btnValue: 0,
      })
    },
  },

  methods: {
    ...mapActions(useGalleryPictureStore, ['unlockPicture']),
    formatResult,

    openDetail(e: PointerEvent): void {
      const target = e.target as Element

      if (target.id === UNLOCK_BTN_ID) return

      const route: RouteObj = {
        name: this.isPicture
          ? this.$getWebView('GalleryPicture')
          : this.$getWebView('GalleryCollection'),
        params: { id: this.itemId },
        query: { redirectFrom: this.$route.query.redirectFrom ?? undefined },
      }

      this.$router.push(route)
    },
  },
})
</script>

<style lang="scss" scoped>
.gallery-collection-item {
  width: 27.125rem;
  height: 22.5rem;
  position: relative;
  text-shadow: 0.06rem 0.1rem 0.05rem rgba(0, 0, 0, 0.46);
  text-transform: uppercase;
  color: #fff;
  line-height: 1;

  @if $isWsm {
    border: solid 0.125rem #5ba3dc;
  }
  @if $isSsm {
    border: solid 0.125rem #4c648f;
  }

  .gallery-collection-item-img {
    position: absolute;
    inset: 4.125rem 0 0 0;
    width: 100%;
    height: 18.375rem;
    background-size: cover;
  }

  .gallery-collection-item-header {
    width: 100%;
    height: 4.125rem;
    background-color: #3e3e84;
    background-size: cover;
    background-position: center;

    @if $isWsm {
      border-bottom: solid 0.125rem #5ba3dc;
    }
    @if $isSsm {
      border-bottom: solid 0.125rem #4c648f;
    }

    position: absolute;
    top: 0;
    left: 0;

    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    text-transform: uppercase;

    .premium-text {
      color: #e6c4ff;
    }
  }

  .gallery-collection-item-bottom-container {
    position: absolute;
    bottom: 0;
    right: 0;
    display: flex;
    justify-content: flex-end;
    align-items: flex-end;
    flex-direction: column;
    margin-right: -0.125rem;
    margin-bottom: -0.125rem;

    .progress-status-position {
      margin-right: 0.75rem;
      margin-bottom: 0.5rem;
    }
    .puzzle-pieces-wrapper {
      display: flex;
      height: 4.5rem;

      .lock-counter {
        width: 7.4375rem;
        background: url($path-images + 'gallery/subheader-bg1.avif') no-repeat;
        background-size: contain;

        display: flex;
        justify-content: center;
        align-items: center;
        gap: 0.75rem;

        @if $isWsm {
          padding-right: 0.8125rem;
        }
        @if $isSsm {
          padding-right: 1.625rem;
        }

        .lock {
          width: 1.875rem;
          height: 2.75rem;
        }
      }
      .puzzle-pieces-counter {
        width: 11rem;
        background: url($path-images + 'gallery/subheader-bg2.avif') no-repeat;
        background-size: contain;
        padding: 0 1.125rem 0 2.25rem;
        @if $isWsm {
          margin-left: -1.25rem;
        }
        @if $isSsm {
          margin-left: -1.6875rem;
        }

        display: flex;
        justify-content: space-evenly;
        align-items: center;

        .accent-color {
          color: #feb942;
        }
        .puzzle {
          width: 3.5rem;
          height: 3.5rem;
        }
      }
    }
    .picture-counter {
      padding: 0 2.0625rem;
      background-color: #0f3251;
    }
    .icon-picture-counter {
      width: 2.9375rem;
      height: 2.3125rem;
    }
  }
  .gallery-collection-item-footer {
    width: 100%;
    height: 4.125rem;
    border: solid 0.125rem rgba(255, 243, 173, 0.9);

    position: absolute;
    bottom: 0;
    left: 0;

    display: flex;
    justify-content: center;
    align-items: center;
    text-transform: uppercase;

    .done {
      width: 3.125rem;
      height: 3.125rem;
      position: absolute;
      right: 0.75rem;
      bottom: 0.25rem;
    }
  }
  .pick-up-message-wrapper {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;

    .pick-up-message {
      width: 100%;
      height: 5rem;
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 1;
      @if $isWsm {
        background-image: linear-gradient(
          90deg,
          rgba(36, 148, 30, 0) 0%,
          rgba(36, 148, 30, 0.8) 15%,
          rgba(36, 148, 30, 0.8) 85%,
          rgba(36, 148, 30, 0) 100%
        );
      }
      @if $isSsm {
        background-image: linear-gradient(
          90deg,
          rgba(14, 231, 204, 0) 0%,
          rgba(14, 231, 204, 0.8) 15%,
          rgba(14, 231, 204, 0.8) 85%,
          rgba(14, 231, 204, 0) 100%
        );
      }
    }
  }

  .flag-new {
    width: 4.5625rem;
    height: 1.875rem;
    background-image: linear-gradient(to top, #ed1616, #ff3f3f);

    position: absolute;
    left: -0.625rem;
    top: 4.875rem;

    display: flex;
    justify-content: center;
    align-items: center;
  }
  .gem-button-wrapper {
    position: absolute;
    bottom: 0.75rem;
    left: 0;
    right: 0;

    display: flex;
    justify-content: center;
  }
  .notification {
    width: 2.0625rem;
    height: 2.0625rem;
    background-image: linear-gradient(to top, #e70000, #ff5b5b);
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: -1rem;
    right: -1rem;
  }
}
</style>
